/**
 * Date Formatting Utility
 * Converts dates between different formats based on user preference
 */

/**
 * Parse a date string in any supported format and return a Date object
 * @param {string} dateStr - Date string in DD/MM/YYYY, MM/DD/YYYY, or YYYY-MM-DD format
 * @returns {Date|null} Date object or null if invalid
 */
export function parseDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') {
    return null;
  }

  const trimmed = dateStr.trim();

  // Handle YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Handle DD/MM/YYYY or MM/DD/YYYY format
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(trimmed)) {
    const parts = trimmed.split('/').map(Number);

    // Try DD/MM/YYYY first (international format)
    const dayFirst = new Date(parts[2], parts[1] - 1, parts[0]);

    // Try MM/DD/YYYY (US format)
    const monthFirst = new Date(parts[2], parts[0] - 1, parts[1]);

    // Use the format that results in a valid date
    // Prefer international format if both are valid
    if (!isNaN(dayFirst.getTime()) && parts[0] <= 31) {
      return dayFirst;
    }
    if (!isNaN(monthFirst.getTime())) {
      return monthFirst;
    }
  }

  return null;
}

/**
 * Format a date object to a specific format
 * @param {Date} date - Date object to format
 * @param {string} format - Target format: 'MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY-MM-DD'
 * @returns {string} Formatted date string
 */
export function formatDate(date, format) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  switch (format) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    default:
      return `${day}/${month}/${year}`; // Default to international format
  }
}

/**
 * Convert a date string from one format to another
 * @param {string} dateStr - Source date string
 * @param {string} targetFormat - Target format: 'MM/DD/YYYY', 'DD/MM/YYYY', or 'YYYY-MM-DD'
 * @returns {string} Converted date string or original if conversion fails
 */
export function convertDateFormat(dateStr, targetFormat) {
  if (!dateStr || typeof dateStr !== 'string') {
    return dateStr;
  }

  // If already in target format, return as-is
  if (isInFormat(dateStr, targetFormat)) {
    return dateStr;
  }

  const date = parseDate(dateStr);
  if (!date) {
    // If we can't parse it, return original
    return dateStr;
  }

  return formatDate(date, targetFormat);
}

/**
 * Check if a date string is already in the specified format
 * @param {string} dateStr - Date string to check
 * @param {string} format - Format to check against
 * @returns {boolean} True if already in format
 */
function isInFormat(dateStr, format) {
  if (!dateStr || typeof dateStr !== 'string') {
    return false;
  }

  const trimmed = dateStr.trim();

  switch (format) {
    case 'MM/DD/YYYY':
    case 'DD/MM/YYYY':
      return /^\d{2}\/\d{2}\/\d{4}$/.test(trimmed);
    case 'YYYY-MM-DD':
      return /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
    default:
      return false;
  }
}

/**
 * Get a sample date in the specified format for preview
 * @param {string} format - Format string
 * @returns {string} Sample date (March 15, 2024)
 */
export function getSampleDate(format) {
  const sampleDate = new Date(2024, 2, 15); // March 15, 2024
  return formatDate(sampleDate, format);
}

/**
 * Validate if a date string is valid in any supported format
 * @param {string} dateStr - Date string to validate
 * @returns {boolean} True if valid
 */
export function isValidDate(dateStr) {
  const date = parseDate(dateStr);
  return date !== null && !isNaN(date.getTime());
}

export default {
  parseDate,
  formatDate,
  convertDateFormat,
  getSampleDate,
  isValidDate
};
